/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import { Modal } from '@/components/ui'
import { useApi } from '@/hooks'

import { createGeofenceCategory, updateGeofenceCategory, writeFailure, type WriteFailure } from '../api'
import { useInvalidateGeofences } from '../hooks'
import { type GeofenceCategory, LOW_FUEL_CATEGORY_TYPE_ID } from '../types'
import { NAME_MAX_LENGTH, validateCategoryName } from '../validation'

interface CategoryFormModalProps {
  show: boolean
  companyId: number
  /** The category to edit; none to create one. */
  category?: GeofenceCategory
  /** The company's categories, for the unique-name check. */
  categories: GeofenceCategory[]
  onClose: () => void
  onSaved: (category: GeofenceCategory) => void
  onFailed: (failure: WriteFailure) => void
}

/**
 * Create or edit a geofence category: its name and whether it is a low fuel category (type 4,
 * which the low fuel alert looks for). Shared by the categories page and the geofence editor.
 */
export const CategoryFormModal = ({
  show,
  companyId,
  category,
  categories,
  onClose,
  onSaved,
  onFailed,
}: CategoryFormModalProps) => {
  const { api } = useApi()
  const invalidate = useInvalidateGeofences()
  const [name, setName] = useState('')
  const [lowFuel, setLowFuel] = useState(false)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedCategoryId, setSavedCategoryId] = useState<number>()

  useEffect(() => {
    if (!show) return
    setName(category?.name ?? '')
    setLowFuel(category?.typeId === LOW_FUEL_CATEGORY_TYPE_ID)
    setSavedCategoryId(undefined)
    setTouched(false)
  }, [show, category])

  const currentCategoryId = category?.id ?? savedCategoryId
  const otherNames = categories.filter((c) => c.id !== currentCategoryId).map((c) => c.name)
  const error = validateCategoryName(name, otherNames)

  const save = async () => {
    setTouched(true)
    if (error) return
    const body = { name: name.trim(), geofenceCategoryTypeId: lowFuel ? LOW_FUEL_CATEGORY_TYPE_ID : null }
    setSaving(true)
    try {
      const saved = category
        ? await updateGeofenceCategory(api, category.id, body)
        : await createGeofenceCategory(api, companyId, body)
      if (!category) setSavedCategoryId(saved.id)
      await invalidate()
      onSaved(saved)
    } catch (ex) {
      onFailed(writeFailure(ex))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      title={category ? 'Edit category' : 'New category'}
      maxWidth="28rem"
      bodyContent={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <label htmlFor="category-name" className="text-sm font-semibold">
                Name
              </label>
              <span className={`text-xs ${name.length > NAME_MAX_LENGTH ? 'text-error' : 'text-base-content/50'}`}>
                {name.length}/{NAME_MAX_LENGTH}
              </span>
            </div>
            <input
              id="category-name"
              className={`input input-bordered input-sm w-full ${touched && error ? 'input-error' : ''}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="e.g. Distribution centres"
              aria-invalid={touched && !!error}
              autoFocus
            />
            {touched && error && <span className="text-xs text-error">{error}</span>}
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-primary mt-0.5"
              checked={lowFuel}
              onChange={(e) => setLowFuel(e.target.checked)}
            />
            <span className="flex flex-col">
              <span className="text-sm">Low fuel geofence</span>
              <span className="text-xs text-base-content/60">
                Geofences in this category are used by the low fuel alert.
              </span>
            </span>
          </label>
          {/* Lets Enter submit; the visible buttons are the modal's. */}
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      }
      footerButtons={[
        {
          label: saving ? 'Saving…' : 'Save',
          onClick: () => void save(),
          useModalVariant: true,
          isLoading: saving,
          disabled: saving,
        },
        { label: 'Cancel', onClick: onClose, disabled: saving },
      ]}
    />
  )
}
